import { useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import type { WeekDay } from "Types/WeekDay";
import type { BulkHoursConfig } from "../types";
import { DayHoursRow } from "../DayHoursRow/DayHoursRow";
import { DaySelector } from "./DaySelector";
import { QuickSelectButtons } from "./QuickSelectButtons";
import { BulkEditActions } from "./BulkEditActions";
import {
  createEmptyTimeRange,
  normalizeEmptyRangesToClosed,
} from "Utils/OpeningHoursUtils";
import { Resources, useResource } from "Translations/Resources";
import { checkTimeRangesValidity } from "Utils/TimeUtils";

const RESOURCES = Resources.Components.Merchant.Facilities.Form.OpeningHours;

const INITIAL_CONFIG: BulkHoursConfig = {
  ranges: [createEmptyTimeRange()],
  isOpenAllDay: false,
  isClosedAllDay: false,
};

type Props = {
  onApply: (selectedDays: Array<WeekDay>, config: BulkHoursConfig) => void;
  onCancel: () => void;
};

export const BulkEditPanel = ({ onApply, onCancel }: Props) => {
  const [selectedDays, setSelectedDays] = useState<Set<WeekDay>>(new Set());
  const [config, setConfig] = useState<BulkHoursConfig>(INITIAL_CONFIG);
  const { t } = useResource();

  const isConfigValid = checkTimeRangesValidity(config.ranges || []);

  const handleDayToggle = (day: WeekDay) => {
    const newSelected = new Set(selectedDays);
    if (newSelected.has(day)) {
      newSelected.delete(day);
    } else {
      newSelected.add(day);
    }
    setSelectedDays(newSelected);
  };

  const handleQuickSelect = (days: Array<WeekDay>) => {
    setSelectedDays(new Set(days));
  };

  const handleApply = () => {
    const finalConfig = normalizeEmptyRangesToClosed(config);
    onApply(Array.from(selectedDays), finalConfig);
    setSelectedDays(new Set());
  };

  return (
    <Paper sx={{ p: [2, 3], mb: 3, backgroundColor: "#f5f5f5" }}>
      <Typography variant="h6" fontSize={["1rem", "1.25rem"]}>
        {t(RESOURCES.SelectDays)}
      </Typography>
      <DaySelector selectedDays={selectedDays} onToggle={handleDayToggle} />
      <QuickSelectButtons onSelect={handleQuickSelect} />
      <DayHoursRow dayHours={config} onChange={setConfig} />
      <Box mt={4}>
        <BulkEditActions
          onApply={handleApply}
          onCancel={onCancel}
          disabled={selectedDays.size === 0 || !isConfigValid}
        />
      </Box>
    </Paper>
  );
};
